import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Package, CheckCircle, Clock, Truck, AlertCircle, User, HeartPulse, Skull } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import SalesmanDashboard from './SalesmanDashboard';

const SALES_ROLES = ['salesman', 'stock_supervisor', 'crm', 'owner'];

const Dashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_orders: 0, my_orders: 0, action_pending: 0,
    completed: 0, pending_delivery: 0, delivered: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!SALES_ROLES.includes(currentUser?.role)) loadStats();
  }, [currentUser?.role]);

  // Sales roles → personal sales assistant dashboard
  if (SALES_ROLES.includes(currentUser?.role)) {
    return <SalesmanDashboard />;
  }

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <div className={`${bgColor} rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 font-medium">{title}</p>
          <p className={`text-4xl font-bold ${color} mt-2`}>{value}</p>
        </div>
        <div className={`${color} opacity-20`}>
          <Icon className="w-16 h-16" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-emerald-700 p-2 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold truncate">Dashboard</h1>
            <p className="text-xs text-emerald-100">Order statistics and metrics</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading statistics...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Total Orders */}
              <StatCard
                icon={Package}
                title="Total Orders"
                value={stats.total_orders}
                color="text-blue-600"
                bgColor="bg-blue-50"
              />

              {/* My Orders */}
              {currentUser?.role === 'salesman' && (
                <StatCard
                  icon={User}
                  title="My Orders"
                  value={stats.my_orders}
                  color="text-purple-600"
                  bgColor="bg-purple-50"
                />
              )}

              {/* Action Pending */}
              <StatCard
                icon={AlertCircle}
                title="Action Pending"
                value={stats.action_pending}
                color="text-orange-600"
                bgColor="bg-orange-50"
              />

              {/* Completed */}
              <StatCard
                icon={CheckCircle}
                title="Completed"
                value={stats.completed}
                color="text-green-600"
                bgColor="bg-green-50"
              />

              {/* Pending Delivery */}
              <StatCard
                icon={Clock}
                title="Pending Delivery"
                value={stats.pending_delivery}
                color="text-yellow-600"
                bgColor="bg-yellow-50"
              />

              {/* Delivered */}
              <StatCard
                icon={Truck}
                title="Delivered"
                value={stats.delivered}
                color="text-teal-600"
                bgColor="bg-teal-50"
              />

              {/* Lead Health Audit — Admin only shortcut */}
              {currentUser?.role === 'admin' && (
                <div
                  onClick={() => navigate('/admin/lead-health-audit')}
                  className="bg-red-50 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-red-500"
                  data-testid="lead-health-audit-card"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Lead Health Audit</p>
                      <p className="text-lg font-bold text-red-600 mt-2">View Report →</p>
                      <p className="text-xs text-gray-500 mt-1">Pipeline leak diagnostics</p>
                    </div>
                    <div className="text-red-600 opacity-20">
                      <HeartPulse className="w-16 h-16" />
                    </div>
                  </div>
                </div>
              )}

              {/* Rotten Leads — Admin only shortcut */}
              {currentUser?.role === 'admin' && (
                <div
                  onClick={() => navigate('/admin/rotten-leads')}
                  className="bg-orange-50 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-orange-500"
                  data-testid="rotten-leads-card"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Rotten Leads</p>
                      <p className="text-lg font-bold text-orange-600 mt-2">Action Today →</p>
                      <p className="text-xs text-gray-500 mt-1">Top zombies by value × age</p>
                    </div>
                    <div className="text-orange-600 opacity-20">
                      <Skull className="w-16 h-16" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
